import { useCallback, useEffect, useRef, useState } from 'react';

import { useTracker } from '@archipro-website/tracker';
import { TwoPaneModal } from '@rocco/components/two-pane-modal';
import { useUserLoggedIn } from '@modules/user';
import {
    AUTH_SOURCE_PARAM_KEY,
    AUTH_TYPE_PARAM_KEY,
} from '~/modules/root/hook/use-auth-event';
import { AuthModalBrandPane } from '~/modules-rocco/user/component/auth-modal/AuthModalBrandPane';
import { getAuthBrandStatement } from '~/modules-rocco/user/component/auth-modal/auth-intent-copy';
import { isReturningPasskeyDevice } from '~/modules/user/passkey/passkey-device-match';
import { useAuthGateSegment } from '~/modules-rocco/user/auth-gate/use-auth-gate-segment';
import { useGateNavigationReset } from '~/modules-rocco/user/auth-gate/use-gate-navigation-reset';
import {
    trackAuthGateActionResumed,
    trackAuthGateBoardChoiceShown,
} from '~/modules-rocco/user/auth-gate/auth-gate-events';
import {
    useGateSubjectValue,
    type GateSubjectKind,
} from '~/modules-rocco/user/auth-gate/gate-subject';
import {
    resolveGatePaneImage,
    resolveGateThumbnail,
} from '~/modules-rocco/user/auth-gate/gate-pane-image';
import type { FollowSelectorItem } from '~/modules/moodboards/types';
import { DESIGN_BOARDS_URL, resolveBoardUrl } from '../board-url';
import {
    useSaveGate,
    SAVE_GATE_AUTH_SOURCE,
    type SaveGateIntent,
} from '../save-gate-state';
import { SaveGateBoardPicker } from './SaveGateBoardPicker';
import { SaveGateSuccess } from './SaveGateSuccess';
import { assignDocumentLocation } from '~/modules/error/util/document-navigation';

export interface SaveGateModalProps {
    /**
     * Completes the save or follow now that a session exists. For a save the
     * intent carries `board` — the destination picked in the choose-board step.
     * Resolving to `false` means it did not land, so the gate does not claim
     * something was kept when it was not.
     */
    onResume: (intent: SaveGateIntent) => Promise<boolean> | boolean;
    /**
     * Where "Go to Design Boards" leads when the destination board is not
     * known — a follow, or a save whose board slug never arrived.
     */
    boardsHref?: string;
}

/**
 * Where the reader is among the gate's own steps, after the auth segment:
 * `choose-board` picks the destination, `success` reports the save landed.
 * `auth` covers everything up to the segment resolving.
 */
type SaveGateStep = 'auth' | 'choose-board' | 'success';

const SAVE_SUBJECT_NOUNS: Record<GateSubjectKind, string> = {
    product: 'Product',
    project: 'Project',
    professional: 'Professional',
    article: 'Article',
};

const PIN_TYPE_NOUNS: Record<string, string> = {
    ProductPin: 'Product',
    ProjectPin: 'Project',
    ProfessionalPin: 'Professional',
    ArticlePin: 'Article',
    PhotoPin: 'Photo',
};

/**
 * The left pane names the thing being kept, per the choose-board frame. The
 * pin type is what the reader actually clicked, so it wins over the page
 * subject (a photo saved off a project page is still a Photo).
 */
const saveBrandStatement = (
    pinType?: string,
    subjectKind?: GateSubjectKind,
) => {
    const noun =
        (pinType && PIN_TYPE_NOUNS[pinType]) ||
        (subjectKind && SAVE_SUBJECT_NOUNS[subjectKind]);
    return noun
        ? `Save this ${noun} to a Design Board`
        : 'Save to a Design Board';
};

const MODAL_TITLES: Record<SaveGateStep, string> = {
    auth: 'Sign in to save',
    'choose-board': 'Save to Design Board',
    success: 'Saved',
};

/**
 * Design board gating in the revamped two-pane gate: [save attempt] → [auth] →
 * [choose board] → [saved]. Form-less, so the attempt itself is the pre-gate
 * step.
 *
 * A save needs a destination, so once the auth segment resolves the reader
 * picks a board and the save runs into it. A follow has no board target, so it
 * still completes the moment the segment resolves.
 */
export const SaveGateModal = ({
    onResume,
    boardsHref = DESIGN_BOARDS_URL,
}: SaveGateModalProps) => {
    const { intent, isOpen, close } = useSaveGate();
    const { isLoggedIn } = useUserLoggedIn();
    // The open flag is sessionStorage-backed so the modal is still here when
    // the social round-trip lands. Every other navigation has to put it down.
    useGateNavigationReset({
        isOpen,
        authSource: SAVE_GATE_AUTH_SOURCE,
        close,
    });
    const tracker = useTracker();
    const subject = useGateSubjectValue();
    const [step, setStep] = useState<SaveGateStep>('auth');
    const [processingBoardId, setProcessingBoardId] = useState<number | null>(
        null,
    );
    /**
     * The intent whose save landed, held locally for the success screen. The
     * persisted intent is consumed the moment the save lands — otherwise the
     * sessionStorage atoms would re-open the gate on every later page load
     * until the reader explicitly dismissed the success screen.
     */
    /** Last intent shown, so the sheet can animate out after it is cleared. */
    const lastIntentRef = useRef<SaveGateIntent | null>(null);
    const [successIntent, setSuccessIntent] = useState<SaveGateIntent | null>(
        null,
    );
    /** The session was created inside this gate, so greet accordingly. */
    const [isNewAccount, setIsNewAccount] = useState(false);
    /**
     * A social signup reloads the page, so it never runs the in-pane auth
     * success that sets `isNewAccount` above — read the params the redirect
     * appends instead, before useAuthEvent strips them. Without this a brand
     * new account is welcomed *back*.
     *
     * Single use. This modal is mounted site-wide for the whole visit, so a
     * signal kept past the opening it describes would make every later save in
     * that visit read as a fresh signup — greeting a returning member as new,
     * and re-offering Secure Sign-In on a device where they have since enrolled.
     */
    const socialSignUpRef = useRef<boolean>(
        typeof window !== 'undefined' &&
            (() => {
                const params = new URLSearchParams(window.location.search);
                return (
                    params.get(AUTH_SOURCE_PARAM_KEY) ===
                        SAVE_GATE_AUTH_SOURCE &&
                    params.get(AUTH_TYPE_PARAM_KEY) === 'sign-up'
                );
            })(),
    );
    const takeSocialSignUp = useCallback(() => {
        const wasSignUp = socialSignUpRef.current;
        socialSignUpRef.current = false;
        return wasSignUp;
    }, []);
    const resumingRef = useRef(false);
    /** The segment has been entered for this opening of the modal. */
    const enteredRef = useRef(false);
    /** Read by the close effect, which is memoised without `step`. */
    const stepRef = useRef(step);
    stepRef.current = step;
    /**
     * Read by the async resumes, which outlive a dismissal: nothing cancels an
     * in-flight save, and reopening the gate on a success screen behind a
     * reader who has already closed it is not a confirmation, it is an
     * interruption. Same guard the enquiry gates use.
     */
    const isOpenRef = useRef(isOpen);
    isOpenRef.current = isOpen;

    /** The save landed: show success and consume the persisted intent. */
    const showSuccess = useCallback(
        (resumedIntent: SaveGateIntent) => {
            const stillOpen = isOpenRef.current;
            // The save landed and the persisted intent must be consumed either
            // way, or the gate reopens on the next page load. Only the success
            // screen is withheld from a reader who has already left.
            close();
            if (!stillOpen) return;
            setSuccessIntent(resumedIntent);
            setStep('success');
        },
        [close],
    );

    /**
     * The auth segment is behind the reader. A save still needs a destination,
     * so it moves to the board picker; a follow resumes immediately.
     */
    const handleResolved = useCallback(() => {
        if (!intent) return;
        if (intent.action === 'save') {
            setStep('choose-board');
            trackAuthGateBoardChoiceShown({
                tracker,
                authSource: SAVE_GATE_AUTH_SOURCE,
            });
            return;
        }
        if (resumingRef.current) return;
        resumingRef.current = true;
        void (async () => {
            const landed = await onResume(intent);
            resumingRef.current = false;
            trackAuthGateActionResumed({
                tracker,
                authSource: SAVE_GATE_AUTH_SOURCE,
                landed,
                data: { action: intent.action },
            });
            if (landed) {
                showSuccess(intent);
                return;
            }
            // Nothing was kept, so do not tell the reader otherwise.
            close();
        })();
    }, [intent, onResume, close, showSuccess, tracker]);

    /** A board was picked or created; complete the save into it. */
    const handleBoardChosen = useCallback(
        (board: FollowSelectorItem, { isNew }: { isNew: boolean }) => {
            if (!intent || resumingRef.current) return;
            resumingRef.current = true;
            setProcessingBoardId(board.id);
            void (async () => {
                const withBoard = {
                    ...intent,
                    board: {
                        id: board.id,
                        title: board.title,
                        urlSegment: board.urlSegment,
                        projectUrlSegment: board.projectUrlSegment,
                        isPersonal: board.isPersonal,
                        isNew,
                    },
                };
                const landed = await onResume(withBoard);
                resumingRef.current = false;
                setProcessingBoardId(null);
                trackAuthGateActionResumed({
                    tracker,
                    authSource: SAVE_GATE_AUTH_SOURCE,
                    landed,
                    data: { action: intent.action, boardIsNew: isNew },
                });
                if (landed) {
                    showSuccess(withBoard);
                }
                // A save that did not land keeps the picker up so the reader
                // can try again; the resume surfaces its own failure.
            })();
        },
        [intent, onResume, showSuccess, tracker],
    );

    // Written from an effect, not during render: a render-phase ref write can
    // be committed by a pass React later discards, and this ref decides what
    // the sheet shows on its way out.
    useEffect(() => {
        if (intent) lastIntentRef.current = intent;
    }, [intent]);

    const gate = useAuthGateSegment({
        authSource: SAVE_GATE_AUTH_SOURCE,
        onAuthenticated: (result) => setIsNewAccount(!!result?.isSignUp),
        onResolved: handleResolved,
    });

    useEffect(() => {
        if (!isOpen) {
            gate.reset();
            enteredRef.current = false;
            setProcessingBoardId(null);
            resumingRef.current = false;
            // Success stays on screen: consuming the persisted intent closes
            // the atoms while the reader is still reading the outcome. The
            // greeting belongs to the journey that has just ended, so it is
            // cleared with the step — but not out from under a success screen
            // still being read (`dismissSuccess` clears it there).
            if (stepRef.current !== 'success') {
                setStep('auth');
                setIsNewAccount(false);
            }
            return;
        }
        // Placing the reader is a once-per-open decision. Signing in inside the
        // modal flips `isLoggedIn`, and without this the segment would be
        // entered a second time: dismissing an ask already on screen, and
        // resolving again into a step that has already been placed.
        if (enteredRef.current) return;
        enteredRef.current = true;
        if (isLoggedIn) {
            // A social signup has no passkey of its own yet, whatever this
            // browser already holds; a social login usually does. Consumed
            // here, so it describes this opening and no later one.
            const fromSocialSignUp = takeSocialSignUp();
            if (fromSocialSignUp) setIsNewAccount(true);
            gate.enterAuthenticated({ isNewAccount: fromSocialSignUp });
        } else {
            gate.open();
        }
        // The segment's identity changes as it advances, so it stays out of the
        // dependencies; this runs on the modal opening.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, isLoggedIn]);

    /** Leave the success screen; the persisted intent is already consumed. */
    const dismissSuccess = useCallback(() => {
        setSuccessIntent(null);
        setIsNewAccount(false);
        setStep('auth');
    }, []);

    const handleClose = useCallback(() => {
        if (step === 'success') {
            dismissSuccess();
            return;
        }
        gate.close();
        close();
    }, [step, dismissSuccess, gate, close]);

    /** Land on the board the save went into; boards home when unknown. */
    const handleGoToBoards = useCallback(() => {
        const target = resolveBoardUrl(successIntent?.board) ?? boardsHref;
        dismissSuccess();
        assignDocumentLocation(target);
    }, [successIntent, dismissSuccess, boardsHref]);

    /**
     * The intent that was on screen, kept for as long as the sheet is closing.
     *
     * `close()` clears `isOpen` and the intent together — deliberately, so a
     * dismissed gate cannot leave one behind to re-render itself later. The
     * success screen already had `successIntent` to survive that, but a plain
     * dismissal from the auth step had nothing: the modal unmounted the instant
     * it was told to close rather than animating out on `open={false}`, so
     * vaul's `onOpenChange(false)` never fired and it never restored the body
     * scroll position or `pointer-events`. Its unmount fallback bails whenever
     * another drawer is in the DOM, which on iOS left the page unscrollable
     * until a reload.
     */

    const displayIntent = intent ?? successIntent ?? lastIntentRef.current;
    const modalOpen = isOpen || (step === 'success' && successIntent !== null);

    if (!displayIntent) return null;

    const paneImage = resolveGatePaneImage({
        override: displayIntent.image,
        subject,
        gate: `save:${displayIntent.action}`,
    });
    const thumbnail = resolveGateThumbnail({
        override: displayIntent.image,
        subject,
    });
    const statement =
        displayIntent.action === 'save'
            ? saveBrandStatement(displayIntent.pinType, subject?.kind)
            : getAuthBrandStatement('designBoard', isReturningPasskeyDevice());

    return (
        <TwoPaneModal
            corners="rounded"
            open={modalOpen}
            onOpenChange={(nextOpen) => {
                if (!nextOpen) handleClose();
            }}
            title={MODAL_TITLES[step]}
            // The item being kept, not stock brand imagery.
            pane={
                <AuthModalBrandPane statement={statement} image={paneImage} />
            }
        >
            {step === 'success' ? (
                <SaveGateSuccess
                    image={thumbnail}
                    title={displayIntent.title}
                    onGoToBoards={handleGoToBoards}
                    onDismiss={dismissSuccess}
                />
            ) : step === 'choose-board' ? (
                <SaveGateBoardPicker
                    onBoardChosen={handleBoardChosen}
                    processingBoardId={processingBoardId}
                    isNewAccount={isNewAccount}
                    pin={
                        displayIntent.pinId
                            ? {
                                  id: displayIntent.pinId,
                                  pinType: displayIntent.pinType,
                                  libraryItemId: displayIntent.libraryItemId,
                              }
                            : undefined
                    }
                />
            ) : (
                gate.element
            )}
        </TwoPaneModal>
    );
};
